import { useState } from "react";
import { getAlarms } from "../api/client.js";
import { useApi } from "../lib/useApi.js";
import { formatTime } from "../lib/format.js";
import { Shell, type PageId } from "./layout/Shell.js";
import { OverviewPage } from "../pages/OverviewPage.js";
import { CostsPage } from "../pages/CostsPage.js";
import { AlarmsPage } from "../pages/AlarmsPage.js";
import { MetricsPage } from "../pages/MetricsPage.js";
import { UptimePage } from "../pages/UptimePage.js";
import { DeploymentsPage } from "../pages/DeploymentsPage.js";
import { PipelinesPage } from "../pages/PipelinesPage.js";
import { BudgetsPage } from "../pages/BudgetsPage.js";

interface DashboardProps {
  onLogout: () => void;
}

const PAGE_META: Record<PageId, { title: string; subtitle: string }> = {
  overview: {
    title: "Overview",
    subtitle: `All accounts · us-east-1 · ${new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}`,
  },
  costs: { title: "Costs", subtitle: "Cost Explorer · gross unblended · USD" },
  alarms: { title: "Alarms", subtitle: "CloudWatch alarms across 4 accounts" },
  metrics: { title: "Metrics", subtitle: "CloudWatch · 5-minute resolution" },
  uptime: { title: "Uptime & availability", subtitle: "Health checks · regions · SLA 99.9%" },
  deployments: { title: "Deployments", subtitle: "GitHub Actions · environments · read-only" },
  pipelines: { title: "Pipelines", subtitle: "Release · flags & A/B · infra across all Azertica apps" },
  budgets: { title: "Budgets", subtitle: "AWS Budgets · gross usage · credits excluded" },
};

export function Dashboard({ onLogout }: DashboardProps) {
  const [page, setPage] = useState<PageId>("overview");
  const [refreshKey, setRefreshKey] = useState(0);

  // Light fetch for shell chrome (alarm badge + data-as-of); server caches 60s,
  // so this doesn't double AWS calls when AlarmsPage fetches the same endpoint.
  const alarms = useApi(getAlarms, [], refreshKey);
  const alarmCount = alarms.data?.summary.alarm ?? 0;
  const dataAsOf = alarms.data ? formatTime(alarms.data.generatedAt) : null;

  const refresh = () => setRefreshKey((k) => k + 1);
  const meta = PAGE_META[page];

  return (
    <Shell
      page={page}
      onNavigate={setPage}
      alarmCount={alarmCount}
      dataAsOf={dataAsOf}
      onRefresh={refresh}
      onLogout={onLogout}
      title={meta.title}
      subtitle={meta.subtitle}
    >
      {page === "overview" && <OverviewPage refreshKey={refreshKey} onNavigate={setPage} />}
      {page === "costs" && <CostsPage refreshKey={refreshKey} />}
      {page === "alarms" && <AlarmsPage refreshKey={refreshKey} />}
      {page === "metrics" && <MetricsPage refreshKey={refreshKey} />}
      {page === "uptime" && <UptimePage refreshKey={refreshKey} />}
      {page === "deployments" && <DeploymentsPage refreshKey={refreshKey} />}
      {page === "pipelines" && <PipelinesPage refreshKey={refreshKey} />}
      {page === "budgets" && <BudgetsPage refreshKey={refreshKey} />}
    </Shell>
  );
}
